/** Reviews list (Phase 4.1) — модерация отзывов с tabs pending/approved/rejected. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Star, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DeleteConfirmDialog } from "@/components/common/DeleteConfirmDialog";
import { RejectReasonDialog } from "@/components/common/RejectReasonDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";

type ReviewStatus = "pending" | "approved" | "rejected";

interface ReviewImage {
  id: number;
  url: string;
  thumb_url: string;
  position: number;
}

interface AdminReview {
  id: number;
  rating: number;
  title: string | null;
  body: string | null;
  pros: string | null;
  cons: string | null;
  status: ReviewStatus;
  rejected_reason: string | null;
  helpful_count: number;
  is_verified_purchase: boolean;
  moderated_by: number | null;
  moderated_at: string | null;
  images: ReviewImage[];
  client: { id: number; full_name: string | null; phone: string | null };
  product: { id: number; sku: string; name: string | null };
  order_id: number | null;
  created_at: string;
  updated_at: string;
}

interface ReviewsResponse {
  data: AdminReview[];
  pagination: { page: number; per_page: number; total: number; total_pages: number };
}

export function ReviewsListPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<ReviewStatus>("pending");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-reviews", statusFilter],
    queryFn: async () => {
      const { data } = await api.get<ReviewsResponse>("/reviews", {
        params: { status: statusFilter, per_page: 100 },
      });
      return data;
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/reviews/${id}/approve`);
    },
    onSuccess: () => {
      toast.success(t("reviews.approved"));
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? t("common.toast.errorOccurred")),
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => {
      await api.post(`/reviews/${id}/reject`, { reason });
    },
    onSuccess: () => {
      toast.success(t("reviews.rejected"));
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? t("common.toast.errorOccurred")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/reviews/${id}`);
    },
    onSuccess: () => {
      toast.success(t("common.toast.deleted"));
      queryClient.invalidateQueries({ queryKey: ["admin-reviews"] });
    },
  });

  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const items = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading">{t("nav.reviews")}</h1>
        <div className="text-sm text-muted-foreground">
          {data && `${t("reviews.totalLabel")}: ${data.pagination.total}`}
        </div>
      </div>

      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as ReviewStatus)}>
        <TabsList>
          <TabsTrigger value="pending">{t("reviews.tabPending")}</TabsTrigger>
          <TabsTrigger value="approved">{t("reviews.tabApproved")}</TabsTrigger>
          <TabsTrigger value="rejected">{t("reviews.tabRejected")}</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          {t("reviews.empty")}
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((r) => (
            <ReviewCard
              key={r.id}
              review={r}
              onApprove={() => approveMutation.mutate(r.id)}
              onReject={() => setRejectingId(r.id)}
              onDelete={() => setDeletingId(r.id)}
              busy={approveMutation.isPending || rejectMutation.isPending}
            />
          ))}
        </div>
      )}

      <RejectReasonDialog
        open={rejectingId !== null}
        onClose={() => setRejectingId(null)}
        onConfirm={async (reason) => {
          if (rejectingId == null) return;
          await rejectMutation.mutateAsync({ id: rejectingId, reason });
          setRejectingId(null);
        }}
        title={t("reviews.rejectReasonPrompt")}
        description="Причина будет показана автору отзыва."
        confirmLabel="Отклонить"
      />

      <DeleteConfirmDialog
        open={deletingId !== null}
        onClose={() => setDeletingId(null)}
        onConfirm={async () => {
          if (deletingId == null) return;
          await deleteMutation.mutateAsync(deletingId);
          setDeletingId(null);
        }}
        title={t("reviews.confirmDelete")}
        description="Отзыв будет полностью удалён без возможности восстановления."
      />
    </div>
  );
}

function ReviewCard({
  review,
  onApprove,
  onReject,
  onDelete,
  busy,
}: {
  review: AdminReview;
  onApprove: () => void;
  onReject: () => void;
  onDelete: () => void;
  busy: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <Card>
      <CardContent className="pt-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Stars rating={review.rating} />
              {review.is_verified_purchase && (
                <Badge variant="secondary" className="text-xs">
                  {t("reviews.verifiedPurchase")}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">
                {new Date(review.created_at).toLocaleString("ru-RU")}
              </span>
            </div>
            {review.title && <div className="font-medium">{review.title}</div>}
          </div>

          {review.status === "pending" && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="default"
                onClick={onApprove}
                disabled={busy}
              >
                <Check className="w-4 h-4 mr-1" />
                {t("reviews.approve")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onReject}
                disabled={busy}
              >
                <X className="w-4 h-4 mr-1" />
                {t("reviews.reject")}
              </Button>
            </div>
          )}
          {review.status === "approved" && (
            <Button size="sm" variant="ghost" onClick={onDelete}>
              <Trash2 className="w-4 h-4 text-destructive" />
            </Button>
          )}
        </div>

        {review.body && (
          <div className="text-sm whitespace-pre-line">{review.body}</div>
        )}

        {(review.pros || review.cons) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            {review.pros && (
              <div className="rounded-md bg-green-50 dark:bg-green-950/30 p-2">
                <div className="text-xs font-medium text-green-700 dark:text-green-400 mb-0.5">
                  {t("reviews.pros")}
                </div>
                <div className="whitespace-pre-line">{review.pros}</div>
              </div>
            )}
            {review.cons && (
              <div className="rounded-md bg-red-50 dark:bg-red-950/30 p-2">
                <div className="text-xs font-medium text-red-700 dark:text-red-400 mb-0.5">
                  {t("reviews.cons")}
                </div>
                <div className="whitespace-pre-line">{review.cons}</div>
              </div>
            )}
          </div>
        )}

        {review.images.length > 0 && (
          <div className="flex gap-2 overflow-x-auto">
            {review.images.map((img) => (
              <img
                key={img.id}
                src={img.thumb_url}
                alt=""
                className="h-20 w-20 object-cover rounded-md border"
              />
            ))}
          </div>
        )}

        {review.rejected_reason && (
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 p-2 text-sm">
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
              {t("reviews.rejectedReason")}:
            </span>{" "}
            {review.rejected_reason}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1 border-t">
          <span>
            <strong>{t("reviews.client")}:</strong>{" "}
            {review.client.full_name ?? "—"} ({review.client.phone ?? "—"})
          </span>
          <span>
            <strong>{t("reviews.product")}:</strong>{" "}
            <a
              href={`/products/${review.product.id}/edit`}
              className="text-primary hover:underline"
            >
              {review.product.sku}
            </a>
          </span>
          {review.order_id && (
            <span>
              <strong>{t("reviews.order")}:</strong>{" "}
              <a
                href={`/orders/${review.order_id}`}
                className="text-primary hover:underline"
              >
                #{review.order_id}
              </a>
            </span>
          )}
          {review.helpful_count > 0 && (
            <span>👍 {review.helpful_count}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Stars({ rating }: { rating: number }): JSX.Element {
  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={
            i <= rating
              ? "w-4 h-4 fill-amber-400 stroke-amber-400"
              : "w-4 h-4 stroke-muted-foreground"
          }
        />
      ))}
    </div>
  );
}
